import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import type { RoundSnapshot } from "../../domain/round/round-reducer.js";
import { loadRoundSnapshot } from "../api/round-api.js";
import { roundJournal } from "../data/local-database.js";
import { liveRoundVersion } from "./live-round-event.js";

export function useLiveRound(
  accountId: string,
  snapshot: RoundSnapshot | null,
  queued: boolean,
  authenticated: boolean,
  setSnapshot: Dispatch<SetStateAction<RoundSnapshot | null>>,
): string {
  const [message, setMessage] = useState("");
  useEffect(() => {
    if (!authenticated || !snapshot || queued || snapshot.state === "draft")
      return;
    let active = true;
    let appliedVersion = snapshot.version;
    const source = new EventSource(
      `/api/v1/rounds/${snapshot.roundId}/events`,
      { withCredentials: true },
    );
    const receive = (event: MessageEvent<string>) => {
      const roundVersion = liveRoundVersion(event.data);
      if (roundVersion === null || roundVersion <= appliedVersion) {
        setMessage("Live round updates are connected.");
        return;
      }
      setMessage("Refreshing the live score…");
      void loadRoundSnapshot(snapshot.roundId)
        .then(async (fresh) => {
          if (!active || fresh.version <= appliedVersion) return;
          if (!(await roundJournal.acceptLiveSnapshot(accountId, fresh)))
            return;
          appliedVersion = fresh.version;
          if (!active) return;
          setSnapshot((current) =>
            !current || fresh.version > current.version ? fresh : current,
          );
          setMessage("Live score updated.");
        })
        .catch((error: unknown) => {
          if (active) setMessage(errorMessage(error));
        });
    };
    const types = [
      "round.created",
      "round.started",
      "score.set",
      "hole.entry.set",
      "hole.entry.revised",
      "metric.set",
      "result.declared",
      "scorekeeper.transferred",
      "contest.hole.conceded",
      "contest.match.conceded",
      "contest.remainder.agreed",
      "result.correction.proposed",
      "result.correction.responded",
      "result.correction.rejected",
      "result.correction.applied",
      "round.finished",
      "round.abandoned",
    ];
    types.forEach((type) => source.addEventListener(type, receive));
    source.onerror = () => setMessage("Live score may be stale. Reconnecting…");
    return () => {
      active = false;
      source.close();
    };
  }, [
    accountId,
    authenticated,
    queued,
    setSnapshot,
    snapshot?.roundId,
    snapshot?.state,
  ]);
  return message;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "The request failed.";
}
